import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';

const DEFAULT_TITLE = 'Unsaved changes';
const DEFAULT_MESSAGE = 'You have unsaved changes. Would you like to save before leaving?';

const isPlainObject = (value) =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);

// Blank values are treated as "nothing entered". Without this a page that fills
// its own empty defaults in after mount (custom field maps, a starter phone row)
// would look edited the moment it finished loading.
const normalize = (value) => {
  if (Array.isArray(value)) {
    const items = value.map(normalize);
    while (items.length && items[items.length - 1] === undefined) {
      items.pop();
    }
    return items.length ? items : undefined;
  }

  if (isPlainObject(value)) {
    const result = {};
    Object.keys(value).forEach((key) => {
      const normalized = normalize(value[key]);
      if (normalized !== undefined) result[key] = normalized;
    });
    return Object.keys(result).length ? result : undefined;
  }

  if (value === '' || value === null || value === undefined) return undefined;
  if (value instanceof Date) return value.getTime();
  return value;
};

const deepEqual = (a, b) => {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => deepEqual(item, b[index]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) return false;
    return keys.every((key) => key in b && deepEqual(a[key], b[key]));
  }
  return false;
};

// True when `values` differs from `initialValues` in a way the user would
// recognise as an edit.
const hasMeaningfulChanges = (initialValues, values) =>
  !deepEqual(normalize(initialValues), normalize(values));

/**
 * Warns before the page is left with unsaved work — in-app links, the browser
 * Back button, and refresh or tab close.
 *
 * @param {boolean|Function} when      dirty flag, or a function read at navigation time
 * @param {Function} [onSave]          runs the page's own save; omit to hide the save button
 */
const UnsavedChangesGuard = ({
  when,
  onSave,
  title = DEFAULT_TITLE,
  message = DEFAULT_MESSAGE,
  saveLabel = 'Save changes',
}) => {
  const [proceed, setProceed] = useState(null);
  const whenRef = useRef(when);
  whenRef.current = when;

  const isBlocked = useCallback(() => {
    const current = whenRef.current;
    return typeof current === 'function' ? Boolean(current()) : Boolean(current);
  }, []);

  const handleBlocked = useCallback((continueNavigation) => {
    setProceed(() => continueNavigation);
  }, []);

  useNavigationGuard({ isBlocked, onBlocked: handleBlocked });

  if (!proceed) return null;

  const handleLeave = () => {
    const continueNavigation = proceed;
    setProceed(null);
    continueNavigation();
  };

  // Saving cancels the navigation instead of chaining onto it: every one of
  // these pages already goes somewhere sensible once a save succeeds, and a
  // rejected save has to leave the user on the form with the errors.
  const handleSave = () => {
    setProceed(null);
    onSave();
  };

  return (
    <div className="modal-wrapper modal-animate-fade">
      <div className="modal-body modal-animate-scale w-4/5 max-w-xl">
        <TriangleAlert className="size-10" />
        <h2 className="my-2 text-lg font-medium text-gray-900">{title}</h2>
        <p className="mb-2.5 text-sm">{message}</p>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button type="button" onClick={() => setProceed(null)} className="mt-2 btn btn-outline">
            Stay on page
          </button>
          <button
            type="button"
            onClick={handleLeave}
            className="mt-2 btn btn-danger btn-outline"
          >
            Leave without saving
          </button>
          {onSave ? (
            <button type="button" onClick={handleSave} className="mt-2 btn btn-primary">
              {saveLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

UnsavedChangesGuard.propTypes = {
  when: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSave: PropTypes.func,
  title: PropTypes.string,
  message: PropTypes.string,
  saveLabel: PropTypes.string,
};

// These pages keep rewriting their own values after mount — merging custom-field
// defaults, syncing external state back in. Requiring a real click or keystroke
// first means that settling-in period can never be mistaken for an edit.
const useHasUserInteracted = () => {
  const [interacted, setInteracted] = useState(false);

  useEffect(() => {
    if (interacted) return undefined;

    const markInteracted = () => setInteracted(true);
    const events = ['pointerdown', 'keydown'];
    events.forEach((event) => window.addEventListener(event, markInteracted, true));

    return () => {
      events.forEach((event) => window.removeEventListener(event, markInteracted, true));
    };
  }, [interacted]);

  return interacted;
};

/**
 * Drop inside a <Formik> render tree. Dirtiness comes from the form itself;
 * `extraDirty` covers editors that live outside Formik (a form builder, a
 * drag-and-drop email designer) and may be a function when the answer is too
 * expensive to recompute on every render.
 *
 * `saving` must be the page's own in-flight save flag. These pages navigate to
 * their list once a save succeeds, and that navigation happens while the form
 * still looks edited — without this the page would be stopped by its own guard.
 */
const FormikUnsavedChangesGuard = ({
  extraDirty = false,
  canSave = true,
  saving = false,
  ...rest
}) => {
  const { values, initialValues, submitForm, validateForm } = useFormikContext();
  const interacted = useHasUserInteracted();

  // submitForm runs first because it is what marks every field touched — check
  // for errors before that and the warning would point at highlights that are
  // not on screen yet.
  const handleSave = async () => {
    await submitForm();
    const errors = await validateForm();

    if (Object.keys(errors).length) {
      toast.warning('Some fields still need attention.', {
        description: 'Fix the highlighted fields, then save again.',
      });
    }
  };

  // Evaluated only when the user tries to leave, so a costly `extraDirty` check
  // never runs on a keystroke.
  const isDirty = () => {
    if (saving) return false;
    if (typeof extraDirty === 'function' ? extraDirty() : extraDirty) return true;
    return interacted && hasMeaningfulChanges(initialValues, values);
  };

  return (
    <UnsavedChangesGuard
      when={isDirty}
      onSave={canSave ? handleSave : undefined}
      {...rest}
    />
  );
};

FormikUnsavedChangesGuard.propTypes = {
  extraDirty: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  canSave: PropTypes.bool,
  saving: PropTypes.bool,
};

export { UnsavedChangesGuard, FormikUnsavedChangesGuard };
